import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ProfileFormSheet from '../modals/ProfileFormSheet';
import BulkCsvColumnMappingPanel from './BulkCsvColumnMappingPanel';
import BulkCsvOverwriteConfirmPanel from './BulkCsvOverwriteConfirmPanel';
import {
  validateLoginIdentifier,
  validateOptionalPhone
} from '../../utils/contactFieldValidation';
import {
  BULK_USER_CSV_OPTIONAL_COLUMNS,
  BULK_USER_CSV_REQUIRED_COLUMNS,
  buildDefaultOverwriteColumns,
  downloadBulkUserCsvSample,
  readAndValidateBulkUserCsvFile
} from '../../utils/bulkUserCsv';
import {
  BULK_MEMBER_IMPORT_BATCH_SIZE,
  previewBulkOverwriteChanges
} from '../../utils/bulkMemberImport';

const EMPTY_IDENTITY_COLUMNS = {
  userId: { found: false, index: -1, header: null },
  ugpId: { found: false, index: -1, header: null }
};

function resolveUserId(user) {
  if (user == null) return '';
  const id = user.id ?? user.user_id;
  return id != null ? String(id) : '';
}

function toPreviewMember(row) {
  return {
    row_number: row.row_number,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    student_information: row.appData?.student_information || null
  };
}

function mergePreviewCandidates(sourceRows, previewCandidates) {
  const sourceByEmail = new Map(
    (sourceRows || []).map((row) => [String(row.email).toLowerCase(), row])
  );
  return (previewCandidates || [])
    .map((candidate) => {
      const source = sourceByEmail.get(String(candidate.email).toLowerCase());
      if (!source) return null;
      const referenceMaps = source.reference_maps || {};
      const changes = (candidate.changes || []).map((change) => {
        const ref = referenceMaps[change.field];
        if (!ref) return change;
        return {
          ...change,
          to_label: ref.label || change.to_label || null,
          sheet_value: ref.sheetValue || change.sheet_value || null,
          match_method: ref.matchMethod || change.match_method || null
        };
      });
      return {
        ...source,
        changes,
        already_enrolled: candidate.already_enrolled === true,
        will_create_enrollment:
          candidate.will_create_enrollment === true || candidate.already_enrolled === false,
        user_id: candidate.user_id || source.user_id,
        reference_maps: referenceMaps
      };
    })
    .filter(Boolean);
}

function willCreateEnrollment(row) {
  return row?.will_create_enrollment === true || row?.already_enrolled === false;
}

/** Filter a full overwrite preview by currently selected columns (client-side only). */
function filterPreviewBySelectedColumns(previewRows, columnSelection) {
  const enabled = new Set(
    Object.entries(columnSelection || {})
      .filter(([, on]) => on !== false)
      .map(([key]) => key)
  );
  const candidates = [];
  let omittedByColumnFilter = 0;

  for (const row of previewRows || []) {
    const changes = (row.changes || []).filter((change) => enabled.has(change.field));
    // Keep people who still need a new enrollment even if every overwrite column is unchecked.
    if (changes.length === 0 && !willCreateEnrollment(row)) {
      omittedByColumnFilter += 1;
      continue;
    }
    candidates.push({ ...row, changes });
  }

  return { candidates, omittedByColumnFilter };
}

/**
 * Add member sheet: existing user lookup or create new account, then enroll in group-period.
 */
const GroupsAddMemberModal = ({
  open,
  onClose,
  submitting = false,
  groupLabel,
  periodLabel,
  users = [],
  existingEmails = [],
  groupsPeriodsId = null,
  authToken = null,
  loadingUsers = false,
  onAddExisting,
  onCreateNew,
  onBulkCreateNew
}) => {
  const [mode, setMode] = useState('existing');
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [bulkValidatedRows, setBulkValidatedRows] = useState([]);
  const [bulkOverwriteSourceRows, setBulkOverwriteSourceRows] = useState([]);
  const [bulkOverwritePreviewAll, setBulkOverwritePreviewAll] = useState([]);
  const [bulkServerUnchangedCount, setBulkServerUnchangedCount] = useState(0);
  const [bulkValidationErrors, setBulkValidationErrors] = useState([]);
  const [bulkExtraColumnMappings, setBulkExtraColumnMappings] = useState([]);
  const [mappingEnabled, setMappingEnabled] = useState({});
  const [ignoreDuplicates, setIgnoreDuplicates] = useState(false);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [useCsvUserIds, setUseCsvUserIds] = useState(false);
  const [useCsvUgpIds, setUseCsvUgpIds] = useState(false);
  const [identityColumns, setIdentityColumns] = useState(EMPTY_IDENTITY_COLUMNS);
  const [overwriteSelectedEmails, setOverwriteSelectedEmails] = useState({});
  const [overwriteColumns, setOverwriteColumns] = useState({});
  const [bulkSkippedDuplicates, setBulkSkippedDuplicates] = useState([]);
  const [validatingCsv, setValidatingCsv] = useState(false);
  const [previewingOverwrite, setPreviewingOverwrite] = useState(false);
  const [newAccountsOpen, setNewAccountsOpen] = useState(false);
  const [error, setError] = useState('');
  const csvInputRef = useRef(null);
  const wasOpenRef = useRef(false);
  /** First-load apps + track/pathway/vendor lists; reused on mapping/option toggles. */
  const csvLookupCacheRef = useRef({ assignedApps: null, fieldOptions: null });
  const csvValidateSeqRef = useRef(0);

  const clearCsvLookupCache = () => {
    csvLookupCacheRef.current = { assignedApps: null, fieldOptions: null };
  };

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setMode('existing');
      setSearch('');
      setSelectedUserId('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setCsvFile(null);
      setBulkValidatedRows([]);
      setBulkOverwriteSourceRows([]);
      setBulkOverwritePreviewAll([]);
      setBulkServerUnchangedCount(0);
      setBulkValidationErrors([]);
      setBulkExtraColumnMappings([]);
      setMappingEnabled({});
      setIgnoreDuplicates(false);
      setAllowOverwrite(false);
      setUseCsvUserIds(false);
      setUseCsvUgpIds(false);
      setIdentityColumns(EMPTY_IDENTITY_COLUMNS);
      setOverwriteSelectedEmails({});
      setOverwriteColumns({});
      setBulkSkippedDuplicates([]);
      setValidatingCsv(false);
      setPreviewingOverwrite(false);
      setNewAccountsOpen(false);
      setError('');
      clearCsvLookupCache();
      csvValidateSeqRef.current += 1;
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    }
    wasOpenRef.current = open;
  }, [open]);

  const resetBulkState = () => {
    setCsvFile(null);
    setBulkValidatedRows([]);
    setBulkOverwriteSourceRows([]);
    setBulkOverwritePreviewAll([]);
    setBulkServerUnchangedCount(0);
    setBulkValidationErrors([]);
    setBulkExtraColumnMappings([]);
    setMappingEnabled({});
    setUseCsvUserIds(false);
    setUseCsvUgpIds(false);
    setIdentityColumns(EMPTY_IDENTITY_COLUMNS);
    setOverwriteSelectedEmails({});
    setOverwriteColumns({});
    setBulkSkippedDuplicates([]);
    setNewAccountsOpen(false);
    clearCsvLookupCache();
    csvValidateSeqRef.current += 1;
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  const ignoredExtraColumns = useMemo(
    () =>
      Object.entries(mappingEnabled)
        .filter(([, enabled]) => enabled === false)
        .map(([header]) => header),
    [mappingEnabled]
  );

  const refreshOverwritePreview = useCallback(
    async (sourceRows) => {
      if (!groupsPeriodsId || !sourceRows?.length) {
        setBulkOverwritePreviewAll([]);
        setBulkServerUnchangedCount(0);
        setOverwriteSelectedEmails({});
        return;
      }

      setPreviewingOverwrite(true);
      try {
        // Fetch the full change set once (all columns). Column toggles filter this in JS.
        const preview = await previewBulkOverwriteChanges({
          token: authToken,
          groupsPeriodsId,
          overwriteColumns: null,
          members: sourceRows.map(toPreviewMember)
        });
        const merged = mergePreviewCandidates(sourceRows, preview.candidates);
        setBulkOverwritePreviewAll(merged);
        setBulkServerUnchangedCount(preview.unchangedCount || 0);
        setOverwriteSelectedEmails((prev) => {
          const next = {};
          merged.forEach((row) => {
            const key = String(row.email).toLowerCase();
            next[key] = prev[key] !== false;
          });
          return next;
        });
      } catch (err) {
        setBulkOverwritePreviewAll([]);
        setBulkServerUnchangedCount(0);
        setBulkValidationErrors([
          { row: 0, message: err.message || 'Could not preview overwrite changes.' }
        ]);
      } finally {
        setPreviewingOverwrite(false);
      }
    },
    [authToken, groupsPeriodsId]
  );

  const filteredOverwrite = useMemo(
    () => filterPreviewBySelectedColumns(bulkOverwritePreviewAll, overwriteColumns),
    [bulkOverwritePreviewAll, overwriteColumns]
  );
  const bulkOverwriteCandidates = filteredOverwrite.candidates;
  const bulkUnchangedCount =
    bulkServerUnchangedCount + filteredOverwrite.omittedByColumnFilter;

  useEffect(() => {
    if (!bulkOverwriteCandidates.length) return;
    setOverwriteSelectedEmails((prev) => {
      const next = {};
      let changed = false;
      bulkOverwriteCandidates.forEach((row) => {
        const key = String(row.email).toLowerCase();
        const value = prev[key] !== false;
        next[key] = value;
        if (prev[key] === undefined) changed = true;
      });
      // Drop selections for rows no longer visible after column filter.
      for (const key of Object.keys(prev)) {
        if (!(key in next)) changed = true;
      }
      return changed ? next : prev;
    });
  }, [bulkOverwriteCandidates]);

  const validateCsvFile = useCallback(
    async (
      file,
      enabledByHeader = mappingEnabled,
      duplicateOption = ignoreDuplicates,
      overwriteOption = allowOverwrite,
      columnSelection = overwriteColumns,
      userIdOption = useCsvUserIds,
      ugpIdOption = useCsvUgpIds,
      { keepLookupCache = true } = {}
    ) => {
      const seq = ++csvValidateSeqRef.current;
      setValidatingCsv(true);
      setBulkValidatedRows([]);
      setBulkOverwriteSourceRows([]);
      setBulkOverwritePreviewAll([]);
      setBulkServerUnchangedCount(0);
      setBulkValidationErrors([]);
      setBulkSkippedDuplicates([]);
      setError('');

      if (!keepLookupCache) {
        clearCsvLookupCache();
      }

      const ignored = Object.entries(enabledByHeader)
        .filter(([, enabled]) => enabled === false)
        .map(([header]) => header);

      const cached = csvLookupCacheRef.current;

      try {
        const result = await readAndValidateBulkUserCsvFile(file, {
          existingEmails,
          groupsPeriodsId,
          token: authToken,
          ignoredExtraColumns: ignored,
          ignoreDuplicates: duplicateOption,
          overwriteExisting: overwriteOption,
          useUserIds: userIdOption,
          useUgpIds: ugpIdOption,
          assignedApps: cached.assignedApps,
          fieldOptions: cached.fieldOptions || {}
        });

        if (seq !== csvValidateSeqRef.current) return;

        csvLookupCacheRef.current = {
          assignedApps: result.assignedApps ?? cached.assignedApps,
          fieldOptions: result.fieldOptions || cached.fieldOptions || {}
        };

        const mappings = result.extraColumnMappings || [];
        const nextIdentity = result.identityColumns || EMPTY_IDENTITY_COLUMNS;
        setIdentityColumns(nextIdentity);
        if (!nextIdentity.userId?.found) setUseCsvUserIds(false);
        if (!nextIdentity.ugpId?.found) setUseCsvUgpIds(false);
        setBulkExtraColumnMappings(mappings);
        setBulkSkippedDuplicates(result.skippedDuplicates || []);
        setMappingEnabled((prev) => {
          const next = {};
          mappings.forEach((mapping) => {
            next[mapping.csvHeader] = enabledByHeader[mapping.csvHeader] !== false;
          });
          return next;
        });

        const defaults = buildDefaultOverwriteColumns(mappings);
        const nextColumns = { ...defaults };
        Object.keys(defaults).forEach((key) => {
          if (columnSelection?.[key] === false) nextColumns[key] = false;
        });
        setOverwriteColumns(nextColumns);

        const sourceRows = result.overwriteCandidates || [];
        setBulkOverwriteSourceRows(sourceRows);

        if (result.ok) {
          setBulkValidatedRows(result.rows);
          if (overwriteOption && sourceRows.length > 0) {
            await refreshOverwritePreview(sourceRows);
            if (seq !== csvValidateSeqRef.current) return;
          } else {
            setBulkOverwritePreviewAll([]);
            setBulkServerUnchangedCount(0);
            setOverwriteSelectedEmails({});
          }
        } else {
          setBulkValidationErrors(result.errors);
        }
      } catch (err) {
        if (seq !== csvValidateSeqRef.current) return;
        setBulkValidationErrors([
          { row: 0, message: err.message || 'Could not validate CSV.' }
        ]);
      } finally {
        if (seq === csvValidateSeqRef.current) {
          setValidatingCsv(false);
        }
      }
    },
    [
      authToken,
      existingEmails,
      groupsPeriodsId,
      mappingEnabled,
      ignoreDuplicates,
      allowOverwrite,
      overwriteColumns,
      useCsvUserIds,
      useCsvUgpIds,
      refreshOverwritePreview
    ]
  );

  const handleIgnoreDuplicatesToggle = async (checked) => {
    setIgnoreDuplicates(checked);
    if (checked && allowOverwrite) {
      setAllowOverwrite(false);
    }
    if (csvFile) {
      await validateCsvFile(
        csvFile,
        mappingEnabled,
        checked,
        checked ? false : allowOverwrite,
        overwriteColumns,
        useCsvUserIds,
        useCsvUgpIds
      );
    }
  };

  const handleAllowOverwriteToggle = async (checked) => {
    setAllowOverwrite(checked);
    if (checked && ignoreDuplicates) {
      setIgnoreDuplicates(false);
    }
    if (csvFile) {
      await validateCsvFile(
        csvFile,
        mappingEnabled,
        checked ? false : ignoreDuplicates,
        checked,
        overwriteColumns,
        useCsvUserIds,
        useCsvUgpIds
      );
    }
  };

  const handleUseCsvUserIdsToggle = async (checked) => {
    setUseCsvUserIds(checked);
    if (csvFile) {
      await validateCsvFile(
        csvFile,
        mappingEnabled,
        ignoreDuplicates,
        allowOverwrite,
        overwriteColumns,
        checked,
        useCsvUgpIds
      );
    }
  };

  const handleUseCsvUgpIdsToggle = async (checked) => {
    setUseCsvUgpIds(checked);
    if (csvFile) {
      await validateCsvFile(
        csvFile,
        mappingEnabled,
        ignoreDuplicates,
        allowOverwrite,
        overwriteColumns,
        useCsvUserIds,
        checked
      );
    }
  };

  const handleMappingToggle = async (csvHeader, enabled) => {
    const nextEnabled = { ...mappingEnabled, [csvHeader]: enabled };
    setMappingEnabled(nextEnabled);
    if (csvFile) {
      await validateCsvFile(csvFile, nextEnabled);
    }
  };

  const handleOverwriteUserToggle = (emailKey, enabled) => {
    setOverwriteSelectedEmails((prev) => ({ ...prev, [emailKey]: enabled }));
  };

  const handleOverwriteColumnToggle = (columnKey, enabled) => {
    setOverwriteColumns((prev) => ({ ...prev, [columnKey]: enabled }));
  };

  const handleCsvFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    setCsvFile(file);
    if (!file) {
      resetBulkState();
      return;
    }
    // New file — refresh app/option lookups once, then reuse on column toggles.
    await validateCsvFile(
      file,
      mappingEnabled,
      ignoreDuplicates,
      allowOverwrite,
      overwriteColumns,
      useCsvUserIds,
      useCsvUgpIds,
      { keepLookupCache: false }
    );
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      return name.includes(q) || (u.email || '').toLowerCase().includes(q);
    });
  }, [users, search]);

  const selectedUser = useMemo(
    () => users.find((u) => resolveUserId(u) === selectedUserId) || null,
    [users, selectedUserId]
  );

  const selectUser = (user) => {
    const id = resolveUserId(user);
    if (!id) return;
    setSelectedUserId(id);
    setError('');
  };

  const selectedOverwriteRows = useMemo(
    () =>
      bulkOverwriteCandidates.filter(
        (row) => overwriteSelectedEmails[String(row.email).toLowerCase()] !== false
      ),
    [bulkOverwriteCandidates, overwriteSelectedEmails]
  );

  const selectedOverwriteColumnKeys = useMemo(
    () =>
      Object.entries(overwriteColumns)
        .filter(([, enabled]) => enabled !== false)
        .map(([key]) => key),
    [overwriteColumns]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (mode === 'existing') {
        if (!selectedUserId) {
          setError('Select a user to add.');
          return;
        }
        await onAddExisting(selectedUserId);
      } else if (mode === 'create') {
        if (!firstName.trim() || !lastName.trim()) {
          setError('First and last name are required.');
          return;
        }
        const emailCheck = validateLoginIdentifier(email, { required: true });
        if (!emailCheck.ok) {
          setError(emailCheck.message);
          return;
        }
        const phoneCheck = validateOptionalPhone(phone);
        if (!phoneCheck.ok) {
          setError(phoneCheck.message);
          return;
        }
        await onCreateNew({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: emailCheck.value,
          phone: phoneCheck.value
        });
      } else if (mode === 'bulk') {
        if (!csvFile) {
          setError('Choose a CSV file to import.');
          return;
        }
        if (bulkValidationErrors.length > 0) {
          setError('Fix CSV validation errors before importing.');
          return;
        }
        if (bulkValidatedRows.length === 0 && selectedOverwriteRows.length === 0) {
          await validateCsvFile(csvFile);
          setError('CSV must pass validation before importing.');
          return;
        }
        const enrollOnlySelected = selectedOverwriteRows.some(willCreateEnrollment);
        if (
          allowOverwrite &&
          selectedOverwriteRows.length > 0 &&
          selectedOverwriteColumnKeys.length === 0 &&
          !enrollOnlySelected
        ) {
          setError(
            'Select at least one column to update, or keep people who still need a new enrollment in this period.'
          );
          return;
        }
        await onBulkCreateNew({
          rows: bulkValidatedRows,
          overwriteRows: allowOverwrite ? selectedOverwriteRows : [],
          ignoreDuplicates,
          overwriteExisting: allowOverwrite && selectedOverwriteRows.length > 0,
          overwriteColumns: selectedOverwriteColumnKeys,
          skippedDuplicates: bulkSkippedDuplicates
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to add member.');
    }
  };

  const periodSubtext = periodLabel
    ? `Enrolls the user in ${groupLabel} for period ${periodLabel}.`
    : `Enrolls the user in ${groupLabel}.`;

  const totalImportCount = bulkValidatedRows.length + selectedOverwriteRows.length;
  const selectedNewEnrollments = selectedOverwriteRows.filter(willCreateEnrollment).length;
  const selectedExistingUpdates = selectedOverwriteRows.length - selectedNewEnrollments;
  const bulkReady =
    bulkValidationErrors.length === 0 &&
    !validatingCsv &&
    !previewingOverwrite &&
    (bulkValidatedRows.length > 0 || selectedOverwriteRows.length > 0);
  const primaryLabel =
    mode === 'existing'
      ? 'Add user'
      : mode === 'bulk'
        ? selectedOverwriteRows.length > 0 && bulkValidatedRows.length === 0
          ? selectedNewEnrollments > 0 && selectedExistingUpdates === 0
            ? 'Enroll users'
            : selectedNewEnrollments > 0
              ? 'Enroll & update'
              : 'Update users'
          : selectedOverwriteRows.length > 0
            ? selectedNewEnrollments > 0
              ? 'Import & enroll'
              : 'Import & update'
            : 'Import users'
        : 'Create user';
  const submittingLabel =
    mode === 'bulk'
      ? selectedOverwriteRows.length > 0 && bulkValidatedRows.length === 0
        ? selectedNewEnrollments > 0
          ? 'Enrolling…'
          : 'Updating…'
        : selectedOverwriteRows.length > 0
          ? 'Importing…'
          : 'Importing…'
      : 'Adding…';
  const primaryDisabled =
    submitting ||
    validatingCsv ||
    previewingOverwrite ||
    (mode === 'existing'
      ? !selectedUserId
      : mode === 'bulk'
        ? !bulkReady
        : !firstName.trim() || !lastName.trim() || !email.trim());

  return (
    <ProfileFormSheet
      open={open}
      onClose={onClose}
      closeDisabled={submitting}
      title={`Add user — ${groupLabel}`}
      titleId="groups-add-member-title"
      subtext={periodSubtext}
      footer={
        <button
          type="submit"
          form="groups-add-member-form"
          className="profile-sheet-btn profile-sheet-btn--primary"
          disabled={primaryDisabled}
        >
          {submitting ? submittingLabel : primaryLabel}
        </button>
      }
    >
      <form id="groups-add-member-form" onSubmit={handleSubmit}>
        <div className="profile-sheet-toggle" role="tablist" aria-label="Add member mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'existing'}
            className={`profile-sheet-toggle-btn${mode === 'existing' ? ' is-active' : ''}`}
            onClick={() => {
              setMode('existing');
              setError('');
            }}
            disabled={submitting}
          >
            Existing user
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'create'}
            className={`profile-sheet-toggle-btn${mode === 'create' ? ' is-active' : ''}`}
            onClick={() => {
              setMode('create');
              setError('');
            }}
            disabled={submitting}
          >
            Create new user
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'bulk'}
            className={`profile-sheet-toggle-btn${mode === 'bulk' ? ' is-active' : ''}`}
            onClick={() => {
              setMode('bulk');
              setError('');
            }}
            disabled={submitting}
          >
            Bulk import
          </button>
        </div>

        {error ? <div className="profile-sheet-error">{error}</div> : null}

        {mode === 'existing' ? (
          <>
            <div className="profile-sheet-form-group">
              <label className="profile-sheet-label" htmlFor="groups-add-member-search">
                Search
              </label>
              <input
                id="groups-add-member-search"
                type="search"
                className="profile-sheet-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or email…"
                disabled={submitting}
              />
            </div>
            <div className="profile-sheet-form-group">
              <span className="profile-sheet-label" id="groups-add-member-select-label">
                Select user
              </span>
              {loadingUsers ? (
                <p className="profile-sheet-muted">Loading users…</p>
              ) : filteredUsers.length === 0 ? (
                <p className="profile-sheet-muted">
                  {users.length === 0
                    ? 'All users are already in this group-period.'
                    : 'No users match your search.'}
                </p>
              ) : (
                <div
                  className="profile-sheet-results"
                  role="listbox"
                  aria-labelledby="groups-add-member-select-label"
                >
                  {filteredUsers.slice(0, 50).map((u) => {
                    const uid = resolveUserId(u);
                    const isSelected = Boolean(uid) && selectedUserId === uid;
                    const displayName =
                      [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Unnamed';
                    return (
                      <div
                        key={uid || displayName}
                        role="option"
                        tabIndex={submitting ? -1 : 0}
                        aria-selected={isSelected}
                        className={`profile-sheet-result-item${isSelected ? ' is-selected' : ''}`}
                        onClick={() => !submitting && selectUser(u)}
                        onKeyDown={(e) => {
                          if (submitting) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectUser(u);
                          }
                        }}
                      >
                        <span className="profile-sheet-result-name">{displayName}</span>
                        <span className="profile-sheet-result-meta">{u.email || '—'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedUser ? (
                <p className="profile-sheet-selected-hint">
                  Selected:{' '}
                  <strong>
                    {[selectedUser.first_name, selectedUser.last_name].filter(Boolean).join(' ') ||
                      selectedUser.email ||
                      'User'}
                  </strong>
                </p>
              ) : null}
            </div>
          </>
        ) : mode === 'bulk' ? (
          <>
            <p className="profile-sheet-hint">
              Required columns: {BULK_USER_CSV_REQUIRED_COLUMNS.join(', ')}. Optional:{' '}
              {BULK_USER_CSV_OPTIONAL_COLUMNS.join(', ')}. You may add extra columns (for example{' '}
              <code>grade_level</code>, <code>track</code>, or <code>Vendor</code>) when they match
              fields enabled on apps assigned to this group-period. Track values like <code>A</code>{' '}
              map to periods titled <strong>Track A</strong>; Vendor values like <code>FA01</code>{' '}
              match team group names. Optional identity columns <code>User ID</code> and{' '}
              <code>UGP ID</code> can set the created user and enrollment IDs when enabled below.
              Fields that contain commas must be wrapped in double quotes.
            </p>
            <div className="profile-sheet-form-group">
              <button
                type="button"
                className="profile-sheet-btn profile-sheet-btn--secondary"
                onClick={downloadBulkUserCsvSample}
                disabled={submitting || validatingCsv}
              >
                Download sample CSV
              </button>
            </div>
            <div className="profile-sheet-form-group">
              <label className="profile-sheet-label" htmlFor="groups-add-member-csv">
                CSV file
              </label>
              <input
                ref={csvInputRef}
                id="groups-add-member-csv"
                type="file"
                accept=".csv,text/csv"
                className="profile-sheet-input"
                onChange={handleCsvFileChange}
                disabled={submitting || validatingCsv}
              />
            </div>
            <div className="profile-sheet-form-group">
              <label className="bulk-csv-option-row">
                <input
                  type="checkbox"
                  checked={ignoreDuplicates}
                  onChange={(e) => handleIgnoreDuplicatesToggle(e.target.checked)}
                  disabled={submitting || validatingCsv || !csvFile || allowOverwrite}
                />
                <span>
                  Skip emails already in the system
                </span>
              </label>
            </div>
            <div className="profile-sheet-form-group">
              <label className="bulk-csv-option-row">
                <input
                  type="checkbox"
                  checked={allowOverwrite}
                  onChange={(e) => handleAllowOverwriteToggle(e.target.checked)}
                  disabled={submitting || validatingCsv || !csvFile || ignoreDuplicates}
                />
                <span>
                  Include existing accounts (confirm enrollment and any field updates)
                </span>
              </label>
            </div>
            {identityColumns.userId?.found ? (
              <div className="profile-sheet-form-group">
                <label className="bulk-csv-option-row">
                  <input
                    type="checkbox"
                    checked={useCsvUserIds}
                    onChange={(e) => handleUseCsvUserIdsToggle(e.target.checked)}
                    disabled={submitting || validatingCsv || !csvFile}
                  />
                  <span>
                    Use <code>{identityColumns.userId.header || 'User ID'}</code> as the created
                    user id
                  </span>
                </label>
              </div>
            ) : null}
            {identityColumns.ugpId?.found ? (
              <div className="profile-sheet-form-group">
                <label className="bulk-csv-option-row">
                  <input
                    type="checkbox"
                    checked={useCsvUgpIds}
                    onChange={(e) => handleUseCsvUgpIdsToggle(e.target.checked)}
                    disabled={submitting || validatingCsv || !csvFile}
                  />
                  <span>
                    Use <code>{identityColumns.ugpId.header || 'UGP ID'}</code> as the created
                    enrollment (UGP) id
                  </span>
                </label>
              </div>
            ) : null}
            {validatingCsv || previewingOverwrite ? (
              <p className="profile-sheet-muted">
                {previewingOverwrite && !validatingCsv
                  ? 'Checking which existing users would change…'
                  : 'Validating CSV…'}
              </p>
            ) : null}
            {bulkValidationErrors.length > 0 ? (
              <div className="profile-sheet-error" role="alert">
                <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
                  Fix these issues before importing:
                </p>
                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                  {bulkValidationErrors.slice(0, 20).map((item, index) => (
                    <li key={`${item.row}-${index}`}>
                      {item.row > 0 ? `Row ${item.row}: ` : ''}
                      {item.message}
                    </li>
                  ))}
                </ul>
                {bulkValidationErrors.length > 20 ? (
                  <p className="profile-sheet-muted" style={{ margin: '6px 0 0' }}>
                    …and {bulkValidationErrors.length - 20} more.
                  </p>
                ) : null}
              </div>
            ) : null}
            {bulkExtraColumnMappings.length > 0 ? (
              <BulkCsvColumnMappingPanel
                mappings={bulkExtraColumnMappings}
                mappingEnabled={mappingEnabled}
                onMappingToggle={handleMappingToggle}
                groupLabel={groupLabel}
                periodLabel={periodLabel}
                disabled={submitting}
              />
            ) : null}
            {bulkValidatedRows.length > 0 ? (
              <div className="bulk-csv-ready-sections">
                <div className="bulk-csv-people-section bulk-csv-people-section--ready">
                  <button
                    type="button"
                    className="bulk-csv-people-section-toggle"
                    aria-expanded={newAccountsOpen}
                    aria-controls="bulk-csv-new-accounts-body"
                    id="bulk-csv-new-accounts-toggle"
                    onClick={() => setNewAccountsOpen((open) => !open)}
                    disabled={submitting || validatingCsv}
                  >
                    <span className="bulk-csv-people-section-toggle-label">
                      New accounts
                      <span className="bulk-csv-people-section-count">
                        {bulkValidatedRows.length} to create
                      </span>
                    </span>
                    <span className="bulk-csv-people-section-chevron" aria-hidden="true">
                      {newAccountsOpen ? '▾' : '▸'}
                    </span>
                  </button>
                  {newAccountsOpen ? (
                    <ul
                      id="bulk-csv-new-accounts-body"
                      className="bulk-csv-new-accounts-list"
                      aria-labelledby="bulk-csv-new-accounts-toggle"
                    >
                      {bulkValidatedRows.map((row) => {
                        const name =
                          [row.first_name, row.last_name].filter(Boolean).join(' ') || 'User';
                        return (
                          <li key={`new-account-${row.row_number}-${row.email}`}>
                            <strong>{name}</strong>
                            <span className="bulk-csv-overwrite-user-meta"> {row.email}</span>
                            {row.row_number ? (
                              <span className="bulk-csv-new-accounts-row">
                                {' '}
                                · row {row.row_number}
                              </span>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : null}
            {allowOverwrite && bulkOverwriteCandidates.length > 0 ? (
              <BulkCsvOverwriteConfirmPanel
                candidates={bulkOverwriteCandidates}
                selectedEmails={overwriteSelectedEmails}
                onToggleUser={handleOverwriteUserToggle}
                overwriteColumns={overwriteColumns}
                onToggleColumn={handleOverwriteColumnToggle}
                extraColumnMappings={bulkExtraColumnMappings.filter(
                  (m) => mappingEnabled[m.csvHeader] !== false
                )}
                unchangedCount={bulkUnchangedCount}
                groupLabel={groupLabel}
                periodLabel={periodLabel}
                disabled={submitting || validatingCsv || previewingOverwrite}
              />
            ) : null}
            {allowOverwrite &&
            !validatingCsv &&
            !previewingOverwrite &&
            bulkOverwriteSourceRows.length > 0 &&
            bulkOverwriteCandidates.length === 0 ? (
              <p className="profile-sheet-selected-hint">
                {bulkUnchangedCount > 0
                  ? `No existing accounts need action — ${bulkUnchangedCount} are already enrolled here and match the selected columns.`
                  : 'No existing accounts need enrollment or updates for the selected columns.'}
              </p>
            ) : null}
            {bulkReady ? (
              <p className="profile-sheet-selected-hint">
                <strong>{totalImportCount}</strong> user
                {totalImportCount === 1 ? '' : 's'} ready
                {bulkValidatedRows.length > 0 || selectedOverwriteRows.length > 0 ? (
                  <>
                    {' '}
                    (
                    {[
                      bulkValidatedRows.length > 0
                        ? `${bulkValidatedRows.length} new account${
                            bulkValidatedRows.length === 1 ? '' : 's'
                          }`
                        : null,
                      selectedNewEnrollments > 0
                        ? `${selectedNewEnrollments} new enrollment${
                            selectedNewEnrollments === 1 ? '' : 's'
                          }`
                        : null,
                      selectedExistingUpdates > 0
                        ? `${selectedExistingUpdates} update${
                            selectedExistingUpdates === 1 ? '' : 's'
                          }`
                        : null
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    )
                  </>
                ) : null}
                {totalImportCount > BULK_MEMBER_IMPORT_BATCH_SIZE
                  ? ` in ${Math.ceil(totalImportCount / BULK_MEMBER_IMPORT_BATCH_SIZE)} batches`
                  : ''}
                {bulkSkippedDuplicates.length > 0
                  ? ` · ${bulkSkippedDuplicates.length} duplicate${
                      bulkSkippedDuplicates.length === 1 ? '' : 's'
                    } will be skipped`
                  : ''}
                {ignoredExtraColumns.length > 0 ? ' · some columns will be skipped' : ''}.
              </p>
            ) : null}
          </>
        ) : (
          <>
            <div className="profile-sheet-field-row">
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="groups-add-member-first">
                  First name
                </label>
                <input
                  id="groups-add-member-first"
                  className="profile-sheet-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="profile-sheet-form-group">
                <label className="profile-sheet-label" htmlFor="groups-add-member-last">
                  Last name
                </label>
                <input
                  id="groups-add-member-last"
                  className="profile-sheet-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
            </div>
            <div className="profile-sheet-form-group">
              <label className="profile-sheet-label" htmlFor="groups-add-member-email">
                Email
              </label>
              <input
                id="groups-add-member-email"
                type="text"
                className="profile-sheet-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. name@school.org"
                required
                autoComplete="off"
                disabled={submitting}
              />
            </div>
            <div className="profile-sheet-form-group">
              <label className="profile-sheet-label" htmlFor="groups-add-member-phone">
                Phone
              </label>
              <input
                id="groups-add-member-phone"
                type="text"
                className="profile-sheet-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                disabled={submitting}
              />
            </div>
          </>
        )}
      </form>
    </ProfileFormSheet>
  );
};

export default GroupsAddMemberModal;
